import { useLocation } from 'react-router-dom';

/**
 * Version-aware theme hook that returns appropriate color classes based on the current route.
 *
 * - V11 routes (/v11/*): Returns v11-* classes that respect light/dark theme toggle
 * - V9/V9.5 routes: Returns v9-* classes that are always dark (ignore theme toggle)
 *
 * This enables shared components to work across all versions while maintaining
 * version-specific theme behavior.
 */
export function useVersionTheme() {
  const location = useLocation();
  const isV11 = location.pathname.startsWith('/v11');

  if (isV11) {
    // V11: Theme-aware (respects light/dark toggle via CSS variables)
    return {
      bg: 'bg-v11-bg',
      bgSecondary: 'bg-v11-bg-secondary',
      surface: 'bg-v11-surface',
      surfaceHover: 'bg-v11-surface-hover',
      border: 'border-v11-border',
      textPrimary: 'text-v11-text-primary',
      textSecondary: 'text-v11-text-secondary',
      textMuted: 'text-v11-text-muted',
      textTertiary: 'text-v11-text-tertiary',
      accent: 'text-v11-accent',
      primary: 'bg-v11-primary',
    };
  } else {
    // V9/V9.5: Always dark (v9 CSS variables are dark-only, don't respond to .dark class)
    return {
      bg: 'bg-v9-bg',
      bgSecondary: 'bg-v9-bg-secondary',
      surface: 'bg-v9-surface',
      surfaceHover: 'bg-v9-surface-hover',
      border: 'border-v9-border',
      textPrimary: 'text-v9-text-primary',
      textSecondary: 'text-v9-text-secondary',
      textMuted: 'text-v9-text-muted',
      textTertiary: 'text-v9-text-muted', // V9 doesn't have tertiary, use muted
      accent: 'text-blue-400',
      primary: 'bg-blue-500',
    };
  }
}
